import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useTranslation from 'next-translate/useTranslation';
import { v4 as uuidv4 } from 'uuid';
import Link from 'next/link';

import { Input, Button, ErrorModal, Terms } from '@/ui/index';
import { formatPhoneNumber } from '@/utils/formatPhoneNumber';
import {
  actions as cabinetActions,
  hooks as cabinetHooks,
  selectors as cabinetSelectors,
} from '@/domains/cabinet';
import { langFormatter } from '@/utils/languageFormat';
import generateInitialToken from '@/utils/generateInitialToken';

import styles from './LoginForm.module.scss';

export const LoginForm = ({ googleReCaptchaProps }) => {
  const [subscribeTrigger, { error, isFetching }] =
    cabinetHooks.useSubscriberCheck();
  const { phoneNumber } = useSelector(cabinetSelectors.selectCabinet);
  const [otpTrigger, { isFetching: otpFetching }] =
    cabinetHooks.useOtpRequest();
  const [showError, setShowError] = useState(false);
  const dispatch = useDispatch();
  const { t, lang } = useTranslation();
  const formattedLang = langFormatter({ lang });
  const [modalActive, setModalActive] = useState(false);
  const [token, setToken] = useState(null);

  const FormSchema = Yup.object().shape({
    phone: Yup.string()
      .matches(/^\+994-\d{2}-\d{3}-\d{2}-\d{2}$/, t('common:Required'))
      .required(t('common:Required')),
  });

  const getToken = useCallback(async () => {
    if (!googleReCaptchaProps?.executeRecaptcha) {
      return;
    }

    try {
      const token = await googleReCaptchaProps.executeRecaptcha('homepage');
      setToken(token);
    } catch (error) {
      // console.error('reCaptcha execution failed:', error);
    }
  }, [googleReCaptchaProps]);

  useEffect(() => {
    getToken();
  }, [getToken]);

  const cleanedPhoneNumber = (value) => {
    return value.replace(/[+-]/g, '');
  };
  const formik = useFormik({
    initialValues: {
      phone: '',
      terms: false,
    },
    validationSchema: FormSchema,
    isInitialValid: false,
    onSubmit: async (values) => {
      const data = {
        phone: values?.phone,
      };
      dispatch(cabinetActions.setPhoneNumber(cleanedPhoneNumber(data.phone)));
      try {
        const initialToken = generateInitialToken(
          cleanedPhoneNumber(data.phone),
          uuidv4(),
        );
        dispatch(cabinetActions.setInitialToken(initialToken));
        sessionStorage.setItem('initialToken', initialToken);
        await subscribeTrigger({
          msisdn: cleanedPhoneNumber(data.phone),
          lang: formattedLang,
          initialToken,
          recaptcha: token,
        }).unwrap();
        modalHandler();
      } catch (error) {
        setShowError(true);
      } finally {
        formik.resetForm();
      }
    },
  });
  const modalHandler = () => {
    if (formik.isValid) {
      setModalActive(true);
    }
  };

  const formikHandler = async () => {
    try {
      const data = await otpTrigger({
        phoneNumber,
        lang: formattedLang,
      }).unwrap();
      dispatch(cabinetActions.setUuId(data.uuid));
      dispatch(cabinetActions.nextStep());
    } catch (error) {
      setShowError(true);
    }
  };
  const submitHandler = (e) => {
    e.preventDefault();
    formik.setTouched({
      phone: true,
    });
    formik.handleSubmit();
  };

  useEffect(() => {
    formik.validateForm();
    setModalActive(false);
  }, [lang]);

  return (
    <>
      <form
        className={styles.form}
        onSubmit={(e) => submitHandler(e)}
        data-testid='login-form'
        itemScope
        itemType='https://schema.org/Action'
      >
        <div className={styles.loginForm}>
          <Input
            className={styles.input}
            onBlur={formik.handleBlur('phone')}
            error={formik.touched.phone && formik.errors.phone}
            value={formik.values.phone}
            type='tel'
            onChange={(value) => {
              const formattedPhone = formatPhoneNumber(value);
              formik.setFieldValue('phone', formattedPhone);
            }}
            label={t('common:Phone')}
            placeholder={'+994-XX-XXX-XX-XX'}
            mask={'+\\9\\9\\4-99-999-99-99'}
            data-testid={'cabinet-login-input'}
          />
          <Button
            data-testid={'login-continue-button'}
            className={styles.buttonFirst}
            disabled={isFetching}
          >
            {t('common:Continue')}
          </Button>
          <div className={styles.bottomTitle}>
            <span>{t('common:DontHaveBakcell')} </span>
          </div>
          <div className={styles.buttons}>
            <Link href='/esim'>
              <Button
                className={`${styles.button} ${styles.button__outlinebtn}`}
                variant='outline'
                data-testid={'login-getEsim'}
              >
                {t('common:GetEsim')}
              </Button>
            </Link>
            <Link href='/'>
              <Button
                className={`${styles.button} ${styles.button__outlinebtn}`}
                variant='outline'
                data-testid={'login-buy-number'}
              >
                {t('cabinet:common.BuyBakcellNumber')}
              </Button>
            </Link>
          </div>
        </div>
      </form>
      {modalActive && (
        <Terms
          active={modalActive}
          formikHandler={() => {
            formikHandler();
          }}
          innerText={t('cabinet:common.Terms')}
          setActive={() => setModalActive(false)}
          isFetching={otpFetching}
          formik={formik}
        />
      )}
      <ErrorModal
        active={showError}
        icon='/assets/error.png'
        setActive={() => setShowError(false)}
      >
        {error ? error.message : t('common:vacancies.error')}
      </ErrorModal>
    </>
  );
};
